"use client"
import { type CSSProperties, useEffect, useState } from "react"
import Link from "next/link"
import cn from "classnames"
import { gsap } from "gsap"

import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { useSharedInView } from "~/hooks/useSharedInView"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { StatItem, StatsInterface } from "./Stats.interface"
import s from "./Stats.module.scss"

export type StatsViewProps = Omit<StatsInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const StatsView = ({
  stats,
  config,
  moduleIndex,
  className,
  style,
}: StatsViewProps) => {
  const configProps = getModuleConfigProps(config)

  return (
    <section
      className={cn(s.stats, `stack-module-${moduleIndex}`, className)}
      data-module="stats"
      aria-label="Key Performance Indicators"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn("container", "default-grid")}>
        <ul
          className={s.statList}
          style={{ "--cols": Math.min(stats.length, 3) } as CSSProperties}
        >
          {stats.map((stat, index) => (
            <StatItemCard key={index} stat={stat} index={index} />
          ))}
        </ul>
      </div>
    </section>
  )
}

function StatItemCard({ stat, index }: { stat: StatItem; index: number }) {
  const [displayValue, setDisplayValue] = useState(stat.value)
  const { ref: inViewRef, inView } = useSharedInView<HTMLSpanElement>(
    "default",
    { once: true },
  )

  useEffect(() => {
    if (!inView) return

    const rawText = stat.value
    const numMatch = rawText.match(/[\d,]+(?:\.\d+)?/)
    if (!numMatch) return

    const hasThousandsSeparator = numMatch[0].includes(",")
    const numericStr = numMatch[0].replace(/,/g, "")
    const targetNum = parseFloat(numericStr)
    const prefix = rawText.slice(0, rawText.indexOf(numMatch[0]))
    const suffix = rawText.slice(
      rawText.indexOf(numMatch[0]) + numMatch[0].length,
    )
    const hasDecimal = numericStr.includes(".")
    const decimalPlaces = hasDecimal
      ? (numericStr.split(".")[1]?.length ?? 0)
      : 0

    const counter = { val: 0 }
    gsap.to(counter, {
      val: targetNum,
      duration: 1.5,
      ease: "power2.out",
      delay: index * 0.15,
      onUpdate: () => {
        const currentNum = parseFloat(counter.val.toFixed(decimalPlaces))
        const formattedVal = hasThousandsSeparator
          ? currentNum.toLocaleString(undefined, {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces,
            })
          : currentNum.toFixed(decimalPlaces)
        setDisplayValue(`${prefix}${formattedVal}${suffix}`)
      },
    })
  }, [inView, stat.value, index])

  return (
    <FadeIn
      element="li"
      className={s.statColumn}
      delay={index * 0.1}
      aria-label={`${stat.value} ${stat.label}`}
    >
      {stat.link?.url ? (
        <Link
          href={stat.link.url}
          target={stat.link.target || undefined}
          rel={
            stat.link.target === "_blank" ? "noopener noreferrer" : undefined
          }
          className={s.statLink}
          aria-label={`Link to more information about ${stat.value} ${stat.label}`}
        >
          {stat.eyebrow && (
            <span className={s.statEyebrow}>{stat.eyebrow}</span>
          )}
          <span ref={inViewRef} className={s.statValue} aria-hidden="true">
            {displayValue}
          </span>
          <span className={s.statLabel} aria-hidden="true">
            {stat.label}
          </span>
        </Link>
      ) : (
        <p className={s.statContent}>
          {stat.eyebrow && (
            <span className={s.statEyebrow}>{stat.eyebrow}</span>
          )}
          <span ref={inViewRef} className={s.statValue}>
            {displayValue}
          </span>
          <span className={s.statLabel}>{stat.label}</span>
        </p>
      )}
    </FadeIn>
  )
}
